import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Box, Chip, Typography } from '@material-ui/core';
import { getTimeFromXDisplay } from '../../../../helpers';
import { CATEGORIES_JOB, TYPES_JOB } from '../../../../config';
import ApplyButton from '../../../../components/ApplyButton';

const useStyles = makeStyles((theme) => ({
  sidebar: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.125rem',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    color: theme.palette.text.primary
  },
  metadataItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  metadataLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '0.875rem'
  },
  metadataValue: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    textAlign: 'right',
    fontSize: '0.875rem',
    maxWidth: '60%',
    wordBreak: 'break-word'
  },
  icon: {
    fontSize: '1.25rem',
    lineHeight: 1
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  urgentDeadline: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  geoLockChip: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    fontWeight: 600,
    padding: theme.spacing(1.5),
    fontSize: '0.875rem',
    width: '100%',
    justifyContent: 'center',
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.warning.main}`
  }
}));

const JobSidebar = ({ job, isGeoLocked }) => {
  const classes = useStyles();

  // Format date from timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if deadline is urgent (within 7 days)
  const isDeadlineUrgent = (deadline) => {
    if (!deadline) return false;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.floor(
      (deadlineDate - now) / (1000 * 60 * 60 * 24)
    );
    return daysUntilDeadline <= 7 && daysUntilDeadline >= 0;
  };

  // Get job type label
  const getJobType = () => {
    if (!job?.type) return 'Not specified';
    const typeFound = TYPES_JOB.find((type) => type.value === job.type);
    return typeFound ? typeFound.label : job.type;
  };

  // Get category label
  const getCategory = () => {
    if (!job?.category) return 'Not specified';
    const categoryFound = CATEGORIES_JOB.find(
      (cat) => cat.value === job.category
    );
    return categoryFound ? categoryFound.label : job.category;
  };

  // Format salary range
  const getSalaryRange = () => {
    if (!job?.salaryFrom && !job?.salaryTo) return null;
    if (job.salaryFrom && job.salaryTo) {
      return `$${job.salaryFrom.toLocaleString()} - $${job.salaryTo.toLocaleString()}`;
    }
    if (job.salaryFrom) {
      return `From $${job.salaryFrom.toLocaleString()}`;
    }
    if (job.salaryTo) {
      return `Up to $${job.salaryTo.toLocaleString()}`;
    }
    return null;
  };

  const postedDate = job?.createdAt ? formatDate(job.createdAt) : null;
  const applyDeadline = job?.applyDeadline
    ? formatDate(job.applyDeadline)
    : null;
  const isUrgent = isDeadlineUrgent(job?.applyDeadline);
  const jobType = getJobType();
  const category = getCategory();
  const region = job?.regionalRestrictions || 'Anywhere in the World';
  const salaryRange = getSalaryRange();

  return (
    <Box className={classes.sidebar}>
      {/* Geo-lock indicator */}
      {isGeoLocked && (
        <Chip
          icon={
            <span role="img" aria-label="lock">
              🔒
            </span>
          }
          label={`Region-based: ${region}`}
          className={classes.geoLockChip}
        />
      )}

      {/* Apply CTA Button - First thing users see */}
      <ApplyButton job={job} jobId={job.id} />

      {/* Job Details Section */}
      <Typography className={classes.sectionTitle}>About the job</Typography>

      {/* Posted Date */}
      {postedDate && (
        <Box className={classes.metadataItem}>
          <Box className={classes.metadataLabel}>
            <span>Posted</span>
          </Box>
          <Typography className={classes.metadataValue}>
            {getTimeFromXDisplay(job.createdAt)}
          </Typography>
        </Box>
      )}

      {/* Apply Deadline */}
      {applyDeadline && (
        <Box className={classes.metadataItem}>
          <Box className={classes.metadataLabel}>
            <span>Deadline</span>
          </Box>
          <Typography
            className={`${classes.metadataValue} ${
              isUrgent ? classes.urgentDeadline : ''
            }`}>
            {applyDeadline}
            {isUrgent && (
              <span role="img" aria-label="fire">
                {' '}
                🔥
              </span>
            )}
          </Typography>
        </Box>
      )}

      {/* Job Type */}
      <Box className={classes.metadataItem}>
        <Box className={classes.metadataLabel}>
          <span>Job type</span>
        </Box>
        <Typography className={classes.metadataValue}>{jobType}</Typography>
      </Box>

      {/* Category */}
      <Box className={classes.metadataItem}>
        <Box className={classes.metadataLabel}>
          <span>Category</span>
        </Box>
        <Typography className={classes.metadataValue}>{category}</Typography>
      </Box>

      {/* Region */}
      <Box className={classes.metadataItem}>
        <Box className={classes.metadataLabel}>
          <span>Region</span>
        </Box>
        <Typography className={classes.metadataValue}>{region}</Typography>
      </Box>

      {/* Salary Range */}
      {salaryRange && (
        <Box className={classes.metadataItem}>
          <Box className={classes.metadataLabel}>
            <span>Salary</span>
          </Box>
          <Typography className={classes.metadataValue}>
            {salaryRange}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

JobSidebar.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string,
    createdAt: PropTypes.number,
    applyDeadline: PropTypes.number,
    type: PropTypes.string,
    category: PropTypes.string,
    regionalRestrictions: PropTypes.string,
    salaryFrom: PropTypes.number,
    salaryTo: PropTypes.number,
    howToApply: PropTypes.string,
    internalCandidatesEnabled: PropTypes.bool
  }).isRequired,
  isGeoLocked: PropTypes.bool
};

JobSidebar.defaultProps = {
  isGeoLocked: false
};

export default JobSidebar;
